import React, {useCallback, useMemo, useState} from 'react';
import {useStyleCategories} from '../base/hooks';
import {
  getStaticDefaultsForStyleCategories,
  injectGlobalStyle,
} from '../base/functions';
import {BaseInputProps} from './types';
import {useDebugBorders} from '../layout';
import {StyleCategoryName} from '../base/types';
import {getCustomBaseStyle} from '../layout/functions';

const BaseInputStyleCategories: StyleCategoryName[] = [
  'font',
  'padding',
  'border',
  'size',
  'position',
  'flexDirection',
  'flex',
  'visibility',
  'shadow',
  'overflow',
];

export const BaseInput: React.FC<BaseInputProps> = ({
  placeholder,
  value,
  disabled,
  secure,
  onFocus,
  onBlur,
  onChange,
  children,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const debugColorsClassname = useDebugBorders();
  const customStyle = useMemo(() => getCustomBaseStyle(props), [props]);
  const styleClassname = useStyleCategories(
    BaseInputStyleCategories,
    props,
    customStyle,
  );

  const handleOnChange = useCallback(
    // eslint-disable-next-line no-undef
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      onChange ? onChange(newValue) : setInternalValue(newValue);
    },
    [onChange],
  );

  return (
    <input
      placeholder={placeholder}
      value={value ?? internalValue}
      className={`${debugColorsClassname} ${styleClassname}`}
      disabled={disabled}
      type={secure === true ? 'password' : undefined}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={handleOnChange}>
      {children}
    </input>
  );
};

// Turn off borders, allow flexbox sizing
injectGlobalStyle({
  default: {
    '*input': {
      ':focus': {
        outline: 'none',
      },
      borderStyle: 'none',
      margin: 0,
      textDecoration: 'none',
      minWidth: '0px',
      width: '100%',
      ...getStaticDefaultsForStyleCategories(BaseInputStyleCategories),
    },
  },
});
